/**
 * The globe's outlines, as a camera `distance` globe radii from the Earth's centre sees them (a
 * near-side perspective). From far away that is the familiar whole hemisphere; close in, only a cap
 * around the point facing the camera is visible, and it fills the disk, which is how the
 * onboarding brings China up to fill the globe. The disk's edge is always the horizon, scaled to
 * radius 1. Every function here is a worklet: the globe's canvas rebuilds its paths on the UI
 * thread whenever it turns or the camera moves.
 *
 * Points beyond the horizon are hidden; a line that runs over it stops exactly at the edge of the
 * disk (`horizonPoint`).
 */

/** What the paths are drawn into: an `SkPathBuilder`, or a recording in tests. */
export interface OnboardingGlobePathSink {
  close(): unknown;
  lineTo(x: number, y: number): unknown;
  moveTo(x: number, y: number): unknown;
}

/** Where the projected globe sits: its centre and radius, in screen points. */
export type OnboardingGlobeDisk = { centerX: number; centerY: number; radius: number };

/** The camera: the globe's rotation (`onboardingGlobeRotation`) and its distance in globe radii. */
export type OnboardingGlobeCamera = { distance: number; rotation: readonly number[] };

/** Closer than this the horizon shrinks to nothing; the onboarding never comes near it. */
const MIN_DISTANCE = 1.02;

/**
 * The perspective for a camera `distance` globe radii from the centre: points with a depth `z`
 * below `horizonZ` are beyond the horizon, and a visible point's `x` and `y` scale by
 * `(distance - 1) / (distance - z) / limbRadius`, which puts the horizon at radius 1.
 */
export function onboardingGlobePerspective(distance: number): {
  horizonZ: number;
  limbRadius: number;
  safeDistance: number;
} {
  "worklet";
  const safeDistance = Math.max(MIN_DISTANCE, distance);
  const horizonZ = 1 / safeDistance;
  const limbRadius = (Math.sqrt(1 - horizonZ * horizonZ) * safeDistance) / (safeDistance + 1);
  return { horizonZ, limbRadius, safeDistance };
}

/**
 * Where the segment from visible `a` to hidden `b` crosses the horizon (depth `horizonZ`), as a
 * unit vector on the disk's edge (`[x, y]`).
 */
export function horizonPoint(
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
  horizonZ = 0,
): [number, number] {
  "worklet";
  const t = (az - horizonZ) / (az - bz);
  const x = ax + (bx - ax) * t;
  const y = ay + (by - ay) * t;
  const length = Math.hypot(x, y);
  return length === 0 ? [1, 0] : [x / length, y / length];
}

/**
 * Lines on the unit sphere (`[x, y, z, …]`), as `camera` sees them, onto `disk`. Hidden stretches
 * are left out, and with their bounding `caps` (`onboardingGlobeLineCaps`) a line wholly beyond
 * the horizon is skipped without projecting a point of it. A `closed` line (a ring) runs back to
 * its first point.
 */
export function addOnboardingGlobeLines(
  sink: OnboardingGlobePathSink,
  lines: readonly Float32Array[],
  camera: OnboardingGlobeCamera,
  disk: OnboardingGlobeDisk,
  closed = false,
  caps?: Float32Array,
): void {
  "worklet";
  const [m0 = 1, m1 = 0, m2 = 0, m3 = 0, m4 = 1, m5 = 0, m6 = 0, m7 = 0, m8 = 1] = camera.rotation;
  const { horizonZ, limbRadius, safeDistance } = onboardingGlobePerspective(camera.distance);
  // A point is in view within this angle of the point facing the camera.
  const horizonAngle = Math.acos(horizonZ);
  const { centerX, centerY, radius } = disk;
  for (const [lineIndex, line] of lines.entries()) {
    if (caps !== undefined) {
      const capZ =
        m6 * (caps[lineIndex * 4] ?? 0) +
        m7 * (caps[lineIndex * 4 + 1] ?? 0) +
        m8 * (caps[lineIndex * 4 + 2] ?? 0);
      const capRadius = caps[lineIndex * 4 + 3] ?? Math.PI;
      if (Math.acos(Math.min(1, Math.max(-1, capZ))) - capRadius > horizonAngle) {
        continue;
      }
    }
    const count = Math.floor(line.length / 3);
    let isDrawing = false;
    let px = 0;
    let py = 0;
    let pz = -1;
    for (let step = 0; step < count + (closed ? 1 : 0); step += 1) {
      const offset = (step % count) * 3;
      const x = line[offset] ?? 0;
      const y = line[offset + 1] ?? 0;
      const z = line[offset + 2] ?? 0;
      const sx = m0 * x + m1 * y + m2 * z;
      const sy = m3 * x + m4 * y + m5 * z;
      const sz = m6 * x + m7 * y + m8 * z;
      if (sz >= horizonZ) {
        const scale = ((safeDistance - 1) / (safeDistance - sz) / limbRadius) * radius;
        const screenX = centerX + sx * scale;
        const screenY = centerY + sy * scale;
        if (!isDrawing) {
          if (step > 0 && pz < horizonZ) {
            const [hx, hy] = horizonPoint(sx, sy, sz, px, py, pz, horizonZ);
            sink.moveTo(centerX + hx * radius, centerY + hy * radius);
            sink.lineTo(screenX, screenY);
          } else {
            sink.moveTo(screenX, screenY);
          }
          isDrawing = true;
        } else {
          sink.lineTo(screenX, screenY);
        }
      } else if (isDrawing) {
        const [hx, hy] = horizonPoint(px, py, pz, sx, sy, sz, horizonZ);
        sink.lineTo(centerX + hx * radius, centerY + hy * radius);
        isDrawing = false;
      }
      px = sx;
      py = sy;
      pz = sz;
    }
  }
}

/** Where the Learn map's whole extent rests on screen: its top-left and points per map unit. */
export type OnboardingMapFrame = { scale: number; x: number; y: number };

/**
 * China partway from the globe to the Learn map (`progress` 0 → 1): each point of its outline
 * (`closed` rings) or its borders (open lines) travels in a straight line from where the globe
 * shows it to where the map draws it. The two are the same points (see `data/onboarding-globe`),
 * so at 1 this is exactly the map's own silhouette and borders. China faces the camera by then, so
 * every point is on the near side.
 */
export function addOnboardingGlobeMorphLines(
  sink: OnboardingGlobePathSink,
  lines: readonly Float32Array[],
  mapLines: readonly Float32Array[],
  camera: OnboardingGlobeCamera,
  disk: OnboardingGlobeDisk,
  mapFrame: OnboardingMapFrame,
  progress: number,
  closed = false,
): void {
  "worklet";
  const [m0 = 1, m1 = 0, m2 = 0, m3 = 0, m4 = 1, m5 = 0, m6 = 0, m7 = 0, m8 = 1] = camera.rotation;
  const { limbRadius, safeDistance } = onboardingGlobePerspective(camera.distance);
  const { centerX, centerY, radius } = disk;
  for (const [lineIndex, line] of lines.entries()) {
    const mapLine = mapLines[lineIndex];
    if (mapLine === undefined) {
      continue;
    }
    const count = Math.min(Math.floor(line.length / 3), Math.floor(mapLine.length / 2));
    for (let index = 0; index < count; index += 1) {
      const x = line[index * 3] ?? 0;
      const y = line[index * 3 + 1] ?? 0;
      const z = line[index * 3 + 2] ?? 0;
      const sz = m6 * x + m7 * y + m8 * z;
      const scale = ((safeDistance - 1) / (safeDistance - sz) / limbRadius) * radius;
      const globeX = centerX + (m0 * x + m1 * y + m2 * z) * scale;
      const globeY = centerY + (m3 * x + m4 * y + m5 * z) * scale;
      const mapX = mapFrame.x + (mapLine[index * 2] ?? 0) * mapFrame.scale;
      const mapY = mapFrame.y + (mapLine[index * 2 + 1] ?? 0) * mapFrame.scale;
      const pointX = globeX + (mapX - globeX) * progress;
      const pointY = globeY + (mapY - globeY) * progress;
      if (index === 0) {
        sink.moveTo(pointX, pointY);
      } else {
        sink.lineTo(pointX, pointY);
      }
    }
    if (closed) {
      sink.close();
    }
  }
}

/**
 * The bounds of China's outline as `camera` sees it, in disk radii about the disk's centre
 * (`[minX, minY, maxX, maxY]`): what the landing fits to the map's frame before the morph, so the
 * two start out the same size and in the same place.
 */
export function onboardingGlobeRingBounds(
  rings: readonly Float32Array[],
  camera: OnboardingGlobeCamera,
): [number, number, number, number] {
  "worklet";
  const [m0 = 1, m1 = 0, m2 = 0, m3 = 0, m4 = 1, m5 = 0, m6 = 0, m7 = 0, m8 = 1] = camera.rotation;
  const { limbRadius, safeDistance } = onboardingGlobePerspective(camera.distance);
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const ring of rings) {
    for (let index = 0; index + 2 < ring.length; index += 3) {
      const x = ring[index] ?? 0;
      const y = ring[index + 1] ?? 0;
      const z = ring[index + 2] ?? 0;
      const sz = m6 * x + m7 * y + m8 * z;
      const scale = (safeDistance - 1) / (safeDistance - sz) / limbRadius;
      const sx = (m0 * x + m1 * y + m2 * z) * scale;
      const sy = (m3 * x + m4 * y + m5 * z) * scale;
      minX = Math.min(minX, sx);
      minY = Math.min(minY, sy);
      maxX = Math.max(maxX, sx);
      maxY = Math.max(maxY, sy);
    }
  }
  return [minX, minY, maxX, maxY];
}
